import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { RefreshCw, Search, AlertTriangle, Check } from "lucide-react";

import { CarregandoBloco, CarregandoLista } from "@/components/carregando";
import { Input } from "@/components/ui/input";

import CategoriaFiscalCard from "./CategoriaFiscalCard";
import { PlanilhaCardapio } from "../../cardapio-planilha";
import { camposFaltando, parseFiscalConfig } from "../fiscalOptions";

const API = "/api";

const getAuthHeader = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
});

// TODO: ler o regime da configuração fiscal da loja quando ela existir.
// Até lá assume Simples Nacional, que é o caso da maioria dos restaurantes.
const REGIME = "simples";

const FILTROS = [
  { key: "pendentes", label: "Pendentes" },
  { key: "prontos", label: "Prontos" },
  { key: "todos", label: "Todos" },
];

/**
 * Módulo Fiscal → aba CATÁLOGO.
 *
 * Edição em massa do cadastro fiscal do cardápio. Ninguém preenche NCM item a
 * item em 200 produtos, então o preenchimento é por categoria — que é como a
 * classificação fiscal se agrupa na prática.
 *
 * A edição individual continua em Cardápio → Produtos → aba Nota Fiscal.
 */
export default function CatalogoFiscal() {
  const [produtos, setProdutos] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [filtro, setFiltro] = useState("pendentes");
  const [busca, setBusca] = useState("");

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      const res = await axios.get(`${API}/products`, getAuthHeader());
      const lista = (res.data || [])
        // Receita é insumo interno: não vai ao cardápio, logo não emite nota.
        .filter((p) => p.product_type !== "receita" && !p.is_insumo)
        .map((p) => ({
          id: p.id,
          name: p.name,
          category: p.category || "Sem categoria",
          fiscalConfig: parseFiscalConfig(p.fiscal_config),
        }));
      setProdutos(lista);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Não foi possível carregar os produtos");
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  const stats = useMemo(() => {
    const total = produtos.length;
    const prontos = produtos.filter((p) => camposFaltando(p.fiscalConfig, REGIME).length === 0).length;
    return { total, prontos, pendentes: total - prontos, pct: total ? Math.round((prontos / total) * 100) : 0 };
  }, [produtos]);

  const porCategoria = useMemo(() => {
    const termo = busca.trim().toLowerCase();
    const filtrados = produtos.filter((p) => {
      if (termo && !p.name.toLowerCase().includes(termo) && !p.category.toLowerCase().includes(termo)) {
        return false;
      }
      if (filtro === "todos") return true;
      const ok = camposFaltando(p.fiscalConfig, REGIME).length === 0;
      return filtro === "prontos" ? ok : !ok;
    });
    const mapa = new Map();
    for (const p of filtrados) {
      if (!mapa.has(p.category)) mapa.set(p.category, []);
      mapa.get(p.category).push(p);
    }
    return [...mapa.entries()].sort((a, b) => a[0].localeCompare(b[0], "pt-BR"));
  }, [produtos, filtro, busca]);

  const aplicar = async (productIds, patch) => {
    setSalvando(true);
    try {
      const res = await axios.post(
        `${API}/products/fiscal/bulk`,
        { product_ids: productIds, fiscal_config: patch },
        getAuthHeader(),
      );
      const n = res.data?.updated ?? productIds.length;
      toast.success(`${n} ${n === 1 ? "produto atualizado" : "produtos atualizados"}`);
      // Recarrega em vez de mesclar no estado local: o merge acontece no
      // backend, então a fonte da verdade é o que ele devolve.
      await carregar();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao aplicar os campos fiscais");
    } finally {
      setSalvando(false);
    }
  };

  return (
    <div className="space-y-5">
      <div className="flex justify-end flex-wrap gap-2">
        {/* Mesmo botão, popup e rotas da Planilha de Cardápio → Produtos (pedido do
            dono, 02/10/2026): exporta o cardápio inteiro e importa o fiscal em massa. */}
        <PlanilhaCardapio onImportado={carregar} />
        <button
          type="button"
          onClick={carregar}
          disabled={carregando}
          className="flex items-center gap-2 px-3 py-2 rounded-lg border text-sm hover:bg-muted/50 transition-colors"
        >
          <RefreshCw className={`w-4 h-4 ${carregando ? "animate-spin" : ""}`} />
          Atualizar
        </button>
      </div>

      {/* Resumo — na primeira carga pisca em cinza em vez de "0 de 0 produtos" (25/10/2026) */}
      {carregando && !produtos.length ? <CarregandoBloco altura="h-[104px]" /> : (
      <div className="bg-card rounded-2xl border shadow-sm p-5">
        <div className="flex items-center justify-between gap-4 mb-3">
          <div className="flex items-center gap-2.5">
            {stats.pendentes === 0 && stats.total > 0 ? (
              <Check className="w-5 h-5 text-emerald-500" />
            ) : (
              <AlertTriangle className="w-5 h-5 text-amber-500" />
            )}
            <span className="text-sm font-semibold">
              {stats.prontos} de {stats.total} produtos prontos para emitir
            </span>
          </div>
          <span className="text-2xl font-bold tabular-nums">{stats.pct}%</span>
        </div>
        <div className="h-2 rounded-full bg-muted overflow-hidden">
          <div
            className={`h-full rounded-full transition-all ${stats.pendentes === 0 ? "bg-emerald-500" : "bg-amber-500"}`}
            style={{ width: `${stats.pct}%` }}
          />
        </div>
        {stats.pendentes > 0 && (
          <p className="text-[11.5px] text-muted-foreground mt-2.5">
            Faltam {stats.pendentes} {stats.pendentes === 1 ? "produto" : "produtos"}. Abra uma
            categoria abaixo, preencha uma vez e aplique a todos de uma vez.
          </p>
        )}
      </div>
      )}

      {/* Filtros */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex gap-1 bg-muted/50 rounded-xl p-1">
          {FILTROS.map((f) => (
            <button
              key={f.key}
              type="button"
              onClick={() => setFiltro(f.key)}
              className={`px-4 py-1.5 text-sm font-medium rounded-lg transition-all ${
                filtro === f.key
                  ? "bg-background text-foreground shadow-sm border border-border/50"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
        <div className="relative flex-1 min-w-[180px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            placeholder="Buscar produto ou categoria..."
            className="h-9 pl-9 text-sm"
          />
        </div>
      </div>

      {/* Categorias */}
      {carregando && !produtos.length ? (
        <CarregandoLista linhas={5} avatar={false} colunas={1} />
      ) : porCategoria.length === 0 ? (
        <div className="py-16 text-center">
          <Check className="w-8 h-8 text-emerald-500 mx-auto mb-2" />
          <p className="text-sm font-medium">
            {filtro === "pendentes" ? "Nenhum produto pendente." : "Nenhum produto encontrado."}
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            {filtro === "pendentes"
              ? "Todo o cardápio está com o cadastro fiscal completo."
              : "Ajuste a busca ou troque o filtro."}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {porCategoria.map(([categoria, itens]) => (
            <CategoriaFiscalCard
              key={categoria}
              categoria={categoria}
              produtos={itens}
              regime={REGIME}
              onAplicar={aplicar}
              salvando={salvando}
            />
          ))}
        </div>
      )}

      <p className="text-[11px] text-muted-foreground text-center pt-2">
        A classificação fiscal é responsabilidade da contabilidade da loja. Os códigos sugeridos são
        os mais comuns em food service — confirme antes de aplicar.
      </p>
    </div>
  );
}
